import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxPermissionsService } from 'ngx-permissions';
import { Contact } from 'src/app/models/contact.model';
import { Service } from 'src/app/models/service.model';
import { BusinessPurpose } from 'src/app/models/ticket/ticket.model';
import { Permission } from 'src/app/shared/constants/permissions';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  InlineErrors,
  updateInlineErrors,
} from 'src/app/shared/utils/form-validation.util';

@Component({
  selector: 'app-ticket-care',
  templateUrl: './ticket-care.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    SharedModule,
  ],
})
export class TicketCareComponent implements OnChanges {
  @Input() services: Service[];

  @Input() selectedServiceId: string;

  @Input() isLoading: boolean;

  @Input() hasLoaded: boolean;

  @Input() primaryReporters: Contact[];

  @Input() submissionError: boolean;

  @Input() isSubmitting: boolean;

  @Output() submitCareForm = new EventEmitter();

  @Input() errorMessage: string;

  readonly Permission = Permission;

  careFormGroup = new UntypedFormGroup({
    reporter: new UntypedFormControl(''),
    locationOfService: new UntypedFormControl(''),
    service: new UntypedFormControl(''),
    Business_Purpose__c: new UntypedFormControl(''),
    subject: new UntypedFormControl('', Validators.required),
    preferredContactMethod: new UntypedFormControl(''),
    alternateContactMethod: new UntypedFormControl(''),
    alternatePhone: new UntypedFormControl(''),
    description: new UntypedFormControl('', Validators.required),
    identifyIssueBy: new UntypedFormControl('location'),
    countryCode: new UntypedFormControl(''),
  });

  areServicesLoading = signal(true);

  areUsersLoading = signal(true);

  inlineErrors: InlineErrors = {};

  formSubmitAttempted = signal(false);

  private readonly permissionsService = inject(NgxPermissionsService);

  async ngOnInit(): Promise<void> {
    const hasServicesPermission = await this.permissionsService.hasPermission(
      Permission.SERVICES,
    );

    if (!hasServicesPermission) {
      this.areServicesLoading.set(false);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.hasLoaded || changes.services) &&
      this.hasLoaded &&
      !this.services?.length
    ) {
      this.areServicesLoading.set(false);
    }
  }

  private readonly fieldErrors: Record<string, string> = {
    subject: 'Please enter a subject',
    description: 'Please describe your issue',
  };

  canSubmit(): boolean {
    return updateInlineErrors(
      this.careFormGroup,
      this.inlineErrors,
      this.fieldErrors,
    );
  }

  submitTicket(): void {
    this.formSubmitAttempted.set(true);
    if (this.canSubmit()) {
      const request = {
        Business_Purpose__c: BusinessPurpose.CareOther,
        contactId: this.careFormGroup.value.reporter,
        portalcustomcontact__c: this.careFormGroup.value.alternatePhone,
        description: this.careFormGroup.value.description,
        service__c: this.careFormGroup.value.service,
        preferred_contact_method__c:
          this.careFormGroup.value.preferredContactMethod ||
          this.careFormGroup.value.alternateContactMethod,
        subject: this.careFormGroup.value.subject,
      };
      this.submitCareForm.emit(request);
    }
  }

  usersLoaded(): void {
    this.areUsersLoading.set(false);
  }

  servicesLoaded(): void {
    this.areServicesLoading.set(false);
  }
}
